import { useTranslation } from 'react-i18next'
import { currencyFormatter } from '../../../services/helperService'
import { calculateTotalSpent } from '../../../services/import/importProductIdsService'
import { Product } from '../../../stores/appReportsStore'
import { useState } from 'react'
import { SupplierLogo } from '../../ui/SupplierLogo'

interface Props {
  product: Product
}

export default function EditLeftProductCard({ product }: Props) {
  const { t } = useTranslation()
  const [imgSrc, setImgSrc] = useState(product.src)

  const total          = calculateTotalSpent(product)
  const unitInfo       = `${currencyFormatter.format(product.unitPrice)}/${product.unitPriceUnit}`
  const stdAmount      = product.useCombo
    ? product.combo?.standardizedAmount ?? product.standardizedAmount
    : product.standardizedAmount
  const isGramOrMl     = ['g', 'ml'].includes(product.standardUnit)
  const totalQuantity  = isGramOrMl
    ? (product.quantity * stdAmount) / 1000
    :  (product.quantity * stdAmount)
  const qty            = new Intl.NumberFormat(undefined,{minimumFractionDigits:0,maximumFractionDigits:1}).format(totalQuantity)
  const stdUnit        = product.standardUnit === 'g' ? 'kg' : product.standardUnit === 'ml' ? 'ltr' : product.standardUnit

  return (
    <div className="bg-background dark:bg-background-dark rounded-3xl p-4 flex flex-col md:flex-row gap-4">
      {/* image */}
      <div className="mx-auto md:mx-0 border border-borderLightGrey dark:border-borderLightGrey-dark rounded-3xl p-2">
        <img
          src={imgSrc}
          alt={product.name}
          className="w-[200px] h-[200px] object-cover rounded-3xl shrink-0"
          onError={e => {
            e.currentTarget.onerror = null
            setImgSrc('/no_photo.png')
          }}
        />
      </div>

      {/* details */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* qty + id */}
        <div className="flex items-start">
          <div className="w-fit text-sm font-medium rounded-lg bg-textBlue/10 dark:bg-textBlue-dark/10 px-2 py-1 text-textBlue dark:text-textBlue-dark">
            {qty}{stdUnit}
          </div>
          <div className="w-fit text-sm font-medium rounded-lg bg-borderGrey dark:bg-borderGrey-dark px-2 py-1 text-tertiary dark:text-tertiary-dark ml-2">
            ID: {product.articleId}
          </div>
        </div>

        {/* name */}
        <a
          href={product.link}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline text-text dark:text-text-dark text-2xl font-semibold truncate"
          title={product.name}
        >
          {product.name}
        </a>

        {/* brand · logo */}
        <div className="flex items-start pt-2">
          <span className="text-sm text-muted dark:text-muted-dark mr-1">{t('product.brand')}</span>
          <span className="text-sm text-secondary dark:text-secondary-dark font-medium">{product.brand}</span>
          <span className="text-sm text-muted dark:text-muted-dark mx-1 font-semibold">·</span>
          <SupplierLogo
            platform={product.platform}
            alt={product.platform}
            size="small"
            className="inline-block align-middle"
          />
        </div>

        {/* desktop-only price + total */}
        <div className="hidden md:flex flex-col mt-6">
          <span className="text-base text-muted dark:text-muted-dark">{t('product.price')}</span>
          <span className="text-base text-primary dark:text-primary-dark font-semibold">{unitInfo}</span>
          <span className="text-xl font-bold text-textGreen dark:text-textGreen-dark mt-6">
            {currencyFormatter.format(total)}
          </span>
        </div>

        {/* mobile (-md) price | total row */}
        <div className="flex justify-between items-center mt-6 md:hidden">
          <div className="flex flex-col">
            <span className="text-base text-muted dark:text-muted-dark">{t('product.price')}</span>
            <span className="text-base font-semibold text-primary dark:text-primary-dark">{unitInfo}</span>
          </div>
          <span className="text-xl font-bold text-textGreen dark:text-textGreen-dark">
            {currencyFormatter.format(total)}
          </span>
        </div>
      </div>
    </div>
  )
}
